/**
 * Phase 4+ — Compliance, Audit Defense & Risk Hardening Types
 * 
 * CORE PRINCIPLES (NON-NEGOTIABLE):
 * - LINK does not create clinical facts
 * - LINK does not suggest additional procedures
 * - LINK does not upcode or expand scope
 * - LINK does not submit claims
 * - Surgeon remains sole clinical author and decision-maker
 * - LINK provides administrative execution, verification, and traceability only
 */

// ============================================================================
// AUDIT TIMELINE
// ============================================================================

export type AuditTimelineEventType =
  // Phase 1 — Authorization
  | 'authorization_created'
  | 'subset_selected'
  | 'surgeon_attestation'
  | 'authorization_approved'
  // Phase 2.5 — Dictation
  | 'dictation_started'
  | 'dictation_completed'
  | 'dictation_signed'
  // Phase 3 — Reconciliation
  | 'op_note_uploaded'
  | 'op_note_replaced'
  | 'analysis_started'
  | 'analysis_completed'
  | 'proof_map_evaluated'
  | 'clarification_generated'
  | 'clarification_responded'
  | 'clarification_dismissed'
  | 'cpt_confirmed_performed'
  | 'cpt_not_performed'
  | 'cpt_deferred'
  | 'modifier_applied'
  | 'modifier_overridden'
  | 'sequencing_completed'
  // Phase 4 — Export
  | 'export_gates_evaluated'
  | 'export_generated'
  | 'export_failed'
  | 'export_copied'
  // Overrides
  | 'override_applied'
  | 'override_reverted'
  // System
  | 'case_state_changed'
  | 'phi_accessed';

export interface AuditTimelineEvent {
  id: string;
  timestamp: string;
  eventType: AuditTimelineEventType;
  actor: string;
  actorRole: string;
  description: string;
  details?: Record<string, unknown>;
  cptCode?: string;
  /** Whether this event involved PHI access */
  phiAccessed: boolean;
  /** Whether this is an override event */
  isOverride: boolean;
  /** Source of the event data */
  source: 'system' | 'user' | 'surgeon' | 'demo';
}

// ============================================================================
// OVERRIDE GOVERNANCE
// ============================================================================

export interface ComplianceOverride {
  id: string;
  caseId: string;
  cptCode?: string;
  overrideType: OverrideType;
  reason: string;
  previousValue: string;
  newValue: string;
  actorId: string;
  actorEmail: string;
  actorRole: string;
  timestamp: string;
  /** Overrides can never change surgeon dictation or attestation */
  affectsClinicalContent: false;
}

export type OverrideType =
  | 'modifier_override'
  | 'documentation_status_override'
  | 'export_gate_bypass'
  | 'sequencing_override'
  | 'completeness_override';

// ============================================================================
// COMPLIANCE STATUS
// ============================================================================

export interface CaseComplianceStatus {
  caseId: string;
  /** All required attestations present */
  attestationsComplete: boolean;
  /** All CPTs confirmed by surgeon */
  allCptsConfirmed: boolean;
  /** Export gates pass */
  exportGatesPass: boolean;
  /** Any unresolved conflicts */
  hasConflicts: boolean;
  /** Any overrides applied */
  hasOverrides: boolean;
  overrideCount: number;
  /** Audit timeline complete */
  auditTimelineComplete: boolean;
  /** Overall compliance status */
  status: 'compliant' | 'blocked' | 'review_required';
  /** Blocking reasons */
  blockingReasons: string[];
}

// ============================================================================
// COMPLIANCE DISCLAIMER
// ============================================================================

export const COMPLIANCE_DISCLAIMER =
  'This system verifies and reconciles documentation and authorization. ' +
  'It does not provide clinical decision-making or claim submission.';

export const AUDIT_EXPORT_DISCLAIMER =
  'This audit package documents the complete decision trail for this case. ' +
  'All clinical decisions were made by the treating surgeon. ' +
  'LINK provided administrative execution, verification, and traceability only.';

export const DEMO_COMPLIANCE_NOTICE =
  'Demo Mode — Simulated data for illustration. No PHI stored or transmitted. ' +
  'No claims submitted. Not for clinical or billing use.';
